/**
 * Typed product-analytics layer for the web app.
 *
 * Every custom event the web app sends is declared in `AnalyticsEvents` and captured through
 * `track()`, the browser counterpart to `api.utils.analytics.track`. The registry is the
 * tracking plan: an event that is not declared here does not compile, and neither does one
 * sent with the wrong properties. See `docs/analytics.md` for the catalogue.
 *
 * Rules, same as the API:
 * - Names are lowercase snake_case `object_verb`. Never rename an event that already fires.
 * - Track intent the server cannot see (clicks, funnels, filters, abandonment). The outcome of
 *   a mutation is already tracked by the API — do not mirror `job_created` and friends here.
 * - Never send free text a user typed, or a customer's/employee's details. Send lengths,
 *   counts, booleans and enums.
 * - `environment`, `release`, `app`, `route`, `section`, the distinct id and the account group
 *   are attached for you in `./posthog.ts`.
 */
import posthog from "./posthog";

export type ListName =
  | "accounts"
  | "courts"
  | "customers"
  | "dashboard_jobs"
  | "employees"
  | "jobs"
  | "workshifts";

export type EntityType =
  | "account"
  | "court"
  | "customer"
  | "employee"
  | "job"
  | "reservation"
  | "workshift";

type FormProps = { form: string } & Record<string, unknown>;

export interface AnalyticsEvents {
  // --- Forms (`useTrackForm`) ---
  /** First edit of a tracked form: the top of every form funnel. */
  form_started: FormProps;
  form_submitted: FormProps;
  /** `validation`: blocked client-side. `server`: the API rejected or failed the submit. */
  form_submit_failed: FormProps & {
    reason: "validation" | "server";
    /** Names of the invalid fields, for `validation` failures. */
    error_fields?: string[];
  };
  form_abandoned: FormProps;

  // --- Lists (`useListVariables`) ---
  /** Debounced; `query_length` is the search box length, never its contents. */
  list_filtered: {
    list: ListName;
    filter_keys: string[];
    query_length: number;
  };
  list_sorted: { list: ListName; field: string; order: "ASC" | "DESC" };
  list_page_changed: { list: ListName; page: number };

  // --- Jobs ---
  /** Jobs-only predecessor of `list_filtered`; kept so its history stays continuous. */
  jobs_filter_changed: { filter: string } & Record<string, unknown>;
  jobs_bulk_action_clicked: { action: "finish" | "todo"; count: number };
  job_paid_toggled: { paid: boolean };
  job_status_modal_opened: { count: number };

  // --- Deleting records (`DeleteButton`) ---
  record_delete_clicked: { entity: EntityType };
  record_delete_confirmed: { entity: EntityType };
  record_delete_cancelled: { entity: EntityType };

  // --- Marketing site ---
  /** A call-to-action on a public page. `location` is where on the page it sits. */
  cta_clicked: {
    cta: string;
    location: string;
    plan?: string;
    destination?: string;
  };

  // --- Sign-up funnel ---
  signup_step_viewed: { step: number };
  signup_step_completed: { step: number; step_name: string };
  signup_plan_selected: {
    price_id: string;
    plan_name?: string;
    billing_interval?: string;
    amount?: number;
  };
  signup_reset_clicked: { step: number };

  // --- Auth ---
  login_failed: { reason: "invalid_credentials" | "error" };

  // --- Billing ---
  billing_interval_toggled: { interval: "MONTHLY" | "YEARLY" };
  billing_plan_clicked: {
    price_id: string;
    plan_name?: string;
    billing_interval?: string;
    amount?: number;
    is_current_plan: boolean;
  };
  billing_portal_clicked: Record<string, never>;
  payment_method_modal_opened: { mode: "add" | "update" };
  subscribe_modal_opened: { price_id: string; has_payment_method: boolean };

  // --- Reports ---
  report_generated: { report: "payroll"; period_days: number };

  // --- Time clock ---
  clock_modal_opened: { action: "clock_in" | "clock_out" };

  // --- Reservations calendar ---
  calendar_navigated: {
    calendar: "reservations";
    direction:
      | "previous_week"
      | "previous_day"
      | "today"
      | "next_day"
      | "next_week"
      | "date_picked";
  };
  /** A reservation dragged to another slot or court on the calendar. */
  reservation_rescheduled: { court_changed: boolean; time_changed: boolean };
  quick_reservation_opened: Record<string, never>;

  // --- Courts ---
  /** A court dragged to a new spot on the facility map and saved. */
  facility_map_court_moved: Record<string, never>;
  court_modal_opened: { mode: "create" | "edit"; source: string };

  // --- Dashboard ---
  dashboard_action_clicked: { action: string };
}

export type AnalyticsEvent = keyof AnalyticsEvents;

/**
 * Capture a product event. Safe to call when PostHog is not configured (no token), and never
 * throws — analytics must not be able to break the interaction it is describing.
 */
export function track<E extends AnalyticsEvent>(
  event: E,
  ...[properties]: Record<string, never> extends AnalyticsEvents[E]
    ? [properties?: AnalyticsEvents[E]]
    : [properties: AnalyticsEvents[E]]
): void {
  try {
    posthog.capture(event, properties);
  } catch {
    // Swallowed on purpose: see above.
  }
}
